import { useState } from 'react';
import {
  Icon,
  ModalHeader,
  ModalContent,
  ModalActions,
  Button,
  Modal,
  ModalDescription,
} from 'semantic-ui-react';
import { MemberDetailPieModal } from './MemberDetailPieModal';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import AGTable from 'src/components/table/aggrid';
import { useRef } from 'react';

export const DetailPieModal = ({ pieType, title, category, list, open, setOpen, count }) => {
  const gridRef = useRef(null);
  const [membersList, setMembersList] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [membersModalOpen, setMembersModalOpen] = useState(false);
  const [countVisible, setCountVisible] = useState(0);

  const openMembers = (members, groupName) => {
    setMembersList(members);
    setSelectedGroup(groupName);
    setMembersModalOpen(true);
  };

  // Column definitions for AG Grid based on pieType
  const getColumnDefs = () => {
    if (pieType === 'leader' || pieType === 'disciple') {
      return [
        {
          headerName: 'Nombre',
          field: 'name',
          cellRenderer: (params) => {
            const { name, lastName, identification, phone } = params.data;
            return (
              <a
                href={`${window.location.origin}/perfil/crecer?identification=${identification}&phone=${phone}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
              >
                {name} {lastName}
              </a>
            );
          },
          width: 200,
        },
        {
          headerName: 'Identificación',
          field: 'identification',
          width: 150,
        },
        {
          headerName: 'Teléfono',
          field: 'phone',
          width: 130,
        },
        {
          headerName: 'Email',
          field: 'email',
          width: 200,
        },
        {
          headerName: 'Edad',
          field: 'age',
          width: 80,
        },
        {
          headerName:
            pieType === 'leader'
              ? 'Nombre del Grupo que Lidera'
              : 'Nombre del Grupo al que pertenece',
          field: 'groupName',
          width: 180,
        },
        {
          headerName: 'Ocupación',
          field: 'ocupation',
          width: 150,
        },
      ];
    } else if (pieType === 'group') {
      return [
        {
          headerName: 'Nombre del Grupo',
          field: 'groupName',
          width: 180,
        },
        {
          headerName: 'Nombre del Líder',
          field: 'leaderName',
          width: 180,
        },
        {
          headerName: 'Día de discipulado',
          field: 'discipleshipDay',
          width: 150,
        },
        {
          headerName: 'Hora de discipulado',
          field: 'discipleshipTime',
          width: 130,
        },
        {
          headerName: 'Categoría',
          field: 'category',
          width: 150,
        },
        {
          headerName: 'Modalidad',
          field: 'modality',
          width: 120,
        },
        {
          headerName: 'Tipo liderazgo',
          field: 'leaderType',
          width: 160,
        },
        {
          headerName: 'Miembros',
          field: 'members',
          cellRenderer: (params) => {
            const { members, groupName } = params.data;
            return (
              <Button
                size="mini"
                onClick={() => openMembers(members, groupName)}
                style={{ margin: '0', height: '30px', padding: '0 8px' }}
              >
                <Icon name="users" />
              </Button>
            );
          },
          width: 100,
          sortable: false,
          filter: false,
        },
      ];
    }
    return [];
  };

  const exportToExcel = () => {
    const dataToExport = list.map((item) => {
      if (pieType === 'group') {
        return {
          'Nombre del Grupo': item.groupName,
          'Nombre del Líder': item.leaderName,
          'Día de discipulado': item.discipleshipDay,
          'Hora de discipulado': item.discipleshipTime,
          Categoría: item.category,
          Modalidad: item.modality,
          'Tipo liderazgo': item.leaderType,
        };
      } else {
        return {
          Nombre: `${item.name} ${item.lastName}`,
          Identificación: item.identification,
          Teléfono: item.phone,
          Email: item.email,
          Edad: item.age,
          Ocupación: item.ocupation,
          Grupo: item.groupName || item.group || '',
        };
      }
    });

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Datos');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const fileName = `detalle_${pieType}_${category}.xlsx`;
    const fileData = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(fileData, fileName);
  };

  const onGridReady = (params) => {
    if (gridRef.current) {
      gridRef.current.api = params.api;
      gridRef.current.columnApi = params.columnApi;
      if (window.innerWidth > 768) {
        params.api.sizeColumnsToFit();
      }
      const newDisplayedRowCount = gridRef?.current?.api?.paginationGetRowCount();
      setCountVisible(newDisplayedRowCount);
    }
  };

  const onFilterChanged = () => {
    const newDisplayedRowCount = gridRef?.current?.api?.paginationGetRowCount();
    setCountVisible(newDisplayedRowCount);
  };

  return (
    <Modal
      size="fullscreen"
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
      open={open}
      trigger={<Button>Show Modal</Button>}
    >
      <ModalHeader>
        Detalle de {title} en la categoría {category}.
      </ModalHeader>
      <ModalDescription>
        <p style={{ padding: '2rem 0 0 2rem', fontSize: '1.2rem' }}>
          Total de {title}: {count}
        </p>
        <p style={{ padding: '0 0 0 2rem', fontSize: '1.2rem' }}>
          Total de {title} en pantalla: {countVisible}
        </p>
      </ModalDescription>
      <ModalContent scrolling>
        {list && list.length > 0 && (
          <div style={{ height: '70vh', width: '100%' }}>
            <AGTable
              rowData={list}
              tableRef={gridRef}
              rowHeight={60}
              columnDefs={getColumnDefs()}
              pagination
              onGridReady={onGridReady}
              onFilterChanged={onFilterChanged}
              suppressDragLeaveHidesColumns={false}
            />
          </div>
        )}
        {!list && <p>No hay detalles para mostrar.</p>}
      </ModalContent>
      <ModalActions>
        <Button color="green" onClick={exportToExcel}>
          <Icon name="download" />
          Descargar Excel
        </Button>
        <Button color="black" onClick={() => setOpen(false)}>
          Cerrar
        </Button>
      </ModalActions>

      <MemberDetailPieModal
        group={selectedGroup}
        list={membersList}
        open={membersModalOpen}
        setOpen={setMembersModalOpen}
      />
    </Modal>
  );
};
